import {
  type FilterOption,
  RecordFormDialog,
  type RecordFormField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SourcePicker,
} from '@olytic/foundation';
import { useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { objects } from '../config/objects';
import {
  createField,
  defaultSources,
  DERIVABLE_OBJECTS,
  type DerivableObject,
  type DerivedField,
  type FieldInput,
  hasHistory,
  OUTPUT_TYPE_LABELS,
  type OutputType,
  parseOptions,
  sourcesFor,
  sourcesFromText,
  updateField,
} from './derivedApi';

const OUTPUT_OPTIONS: FilterOption[] = Object.entries(OUTPUT_TYPE_LABELS).map(([value, label]) => ({ value, label }));
const OBJECT_OPTIONS: FilterOption[] = DERIVABLE_OBJECTS.map((key) => ({ value: key, label: objects[key].plural }));

export interface DerivedFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The object a new field belongs to; omitted, the form asks (Setup). */
  object?: DerivableObject;
  /** The field to edit; omitted, the form creates one. */
  field?: DerivedField;
  onSaved: (field: DerivedField) => void;
}

/**
 * Define (or change) a field filled from history: what it asks, what kind of
 * answer, and what it reads (its sources, and whether linked records too).
 * Saving a new one backfills it for every record; changing the question or
 * what it reads recomputes the AI's values.
 */
export function DerivedFieldDialog({ open, onOpenChange, object, field, onSaved }: DerivedFieldDialogProps): JSX.Element {
  // In Setup the object is picked in the form; the sources follow it.
  const [picked, setPicked] = useState<DerivableObject | null>(null);
  const target = field?.object ?? object ?? picked;
  const fields = useMemo<RecordFormField[]>(
    () => [
      ...(object || field
        ? []
        : [{
            key: 'object',
            label: 'Object',
            required: true,
            editor: { kind: 'select' as const, options: OBJECT_OPTIONS },
            render: ({ value, onChange, disabled, label }: { value: string; onChange: (value: string) => void; disabled: boolean; label: string }) => (
              <Select
                {...(value ? { value } : {})}
                disabled={disabled}
                onValueChange={(next) => {
                  onChange(next);
                  setPicked(next as DerivableObject);
                }}
              >
                <SelectTrigger aria-label={label}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {OBJECT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ),
          }]),
      { key: 'label', label: 'Name', required: true, editor: { kind: 'text', maxLength: 80 } },
      { key: 'output_type', label: 'Answer Type', required: true, editor: { kind: 'select', options: OUTPUT_OPTIONS } },
      {
        key: 'instruction',
        label: 'What to Find',
        required: true,
        wide: true,
        editor: { kind: 'text', maxLength: 2000 },
        help: 'Ask it as you would a colleague who read every meeting and email: “Who signs off on the budget?”',
      },
      {
        key: 'options',
        label: 'Picklist Values',
        wide: true,
        editor: { kind: 'text', maxLength: 2000 },
        help: 'For a picklist: the answers it may give, separated by commas.',
      },
      {
        key: 'sources',
        label: 'Sources',
        required: true,
        wide: true,
        editor: { kind: 'text' },
        help: target && !hasHistory(target) ? 'Nothing is linked to these records, so it reads their own fields.' : 'It reads only what is ticked.',
        render: ({ value, onChange, disabled, label }) => (
          <SourcePicker
            aria-label={label}
            options={[...sourcesFor(target ?? '')]}
            value={sourcesFromText(value)}
            onChange={(next) => onChange(next.join(','))}
            disabled={disabled || !target}
          />
        ),
      },
      ...(target && !hasHistory(target)
        ? []
        : [{
            key: 'include_related',
            label: 'Linked Records',
            editor: { kind: 'boolean' as const },
            help: 'Also read the history of its account, contacts, deals and projects.',
          }]),
    ],
    [object, field, target],
  );

  const initialValues = useMemo<Record<string, string>>(
    () =>
      field
        ? {
            label: field.label,
            output_type: field.output_type,
            instruction: field.instruction,
            options: field.options.join(', '),
            sources: field.sources.join(','),
            include_related: String(field.include_related),
          }
        : { output_type: 'text', sources: object ? defaultSources(object).join(',') : '', include_related: 'false' },
    [field, object],
  );

  return (
    <RecordFormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setPicked(null);
        onOpenChange(next);
      }}
      title={field ? `Edit ${field.label}` : object ? `New AI column on ${objects[object].plural.toLowerCase()}` : 'New field from history'}
      fields={fields}
      initialValues={initialValues}
      submitLabel={field ? 'Save' : 'Save and fill'}
      onSubmit={async (values) => {
        const input: FieldInput = {
          label: values.label ?? '',
          instruction: values.instruction ?? '',
          output_type: (values.output_type ?? 'text') as OutputType,
          options: parseOptions(values.options ?? null),
          sources: sourcesFromText(values.sources).filter((s) => sourcesFor(target ?? '').some((o) => o.value === s)),
          include_related: values.include_related === 'true' && hasHistory(target ?? ''),
        };
        if (input.sources.length === 0) throw new Error('Choose at least one source.');
        if (input.output_type === 'picklist' && input.options.length === 0) {
          throw new Error('A picklist needs its values, separated by commas.');
        }
        const saved = field
          ? await updateField(authenticatedFetch, field.id, input)
          : await createField(authenticatedFetch, { ...input, object: object ?? (values.object as DerivableObject) });
        onSaved(saved);
      }}
    />
  );
}
